import React, { useState, useEffect } from "react";
import { doc, setDoc, collection, addDoc, updateDoc } from "firebase/firestore";
import { ref, uploadString, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { parseHTMLContent } from "./htmlParsingUtils";
import { Quill } from "react-quill-new";
import "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";

// CSS imports for proper styling (same as ViewAndEditUploadedFile)
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/ImportDocument.css";
import "../../css/QuillOverrides.css";


// Register Quill modules for proper processing
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);

// Quill Core Imports for custom blots
const Inline = Quill.import("blots/inline");
const Block = Quill.import("blots/block");

// Custom Inline Code Block (same as ViewAndEditUploadedFile)
class InlineCode extends Inline {
    static create() {
        const node = super.create();
        node.setAttribute("class", "inline-code");
        return node;
    }
    static formats(node) {
        return true;
    }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote (same as ViewAndEditUploadedFile)
class MultiLineBlockquote extends Block {
    static create() {
        const node = super.create();
        node.setAttribute("class", "multi-line-blockquote");
        return node;
    }
    static formats(node) {
        return true;
    }
}
MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "multi-line-blockquote";
MultiLineBlockquote.className = "multi-line-blockquote";

// Register Custom Quill Components
Quill.register(MultiLineBlockquote);
Quill.register(InlineCode);

function ProcessSpaceImport({ extractedData, spaceConfig, userInfo, onComplete, onBack }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [progress, setProgress] = useState({
        currentStep: 'initializing',
        currentFile: '',
        processedFiles: 0,
        totalFiles: 0,
        processedImages: 0,
        totalImages: 0,
        errors: [],
        createdSpace: null,
        createdPages: []
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadedFileRefs, setUploadedFileRefs] = useState([]); // Track uploaded files for cleanup

    // Get current user from Firebase Auth
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName
                });
                console.log("Current user set:", user.uid);
            } else {
                setCurrentUser(null);
                console.log("No user authenticated");
            }
        });

        return () => unsubscribe();
    }, []);

    // Theme Configuration Effect

    // Debug logging for userInfo (fallback)
    useEffect(() => {
        console.log("ProcessSpaceImport - userInfo prop:", userInfo);
        console.log("ProcessSpaceImport - currentUser state:", currentUser);
    }, [userInfo, currentUser]);

    useEffect(() => {
        if (extractedData && spaceConfig && (currentUser || userInfo) && !isProcessing) {
            startImportProcess();
        }
    }, [extractedData, spaceConfig, currentUser, userInfo]);

    const startImportProcess = async () => {
        setIsProcessing(true);
        
        const selectedFiles = extractedData.files.filter(file => 
            spaceConfig.selectedFiles[file.path]
        );

        setProgress(prev => ({
            ...prev,
            currentStep: 'creating_space',
            totalFiles: selectedFiles.length,
            totalImages: extractedData.images.length
        }));

        try {
            // Step 1: Create the space
            const spaceId = await createSpace();
            
            setProgress(prev => ({
                ...prev,
                currentStep: 'processing_files',
                createdSpace: spaceId
            }));

            // Step 2: Process images if enabled
            let imageMap = {};
            if (spaceConfig.processImages && extractedData.images.length > 0) {
                imageMap = await processImages();
            }

            // Step 3: Process HTML files
            await processFiles(selectedFiles, spaceId, imageMap);

            // Check if we successfully created the space and at least some pages
            if (spaceId && progress.createdPages.length > 0) {
                // Success! Skip error reporting and proceed to completion
                setProgress(prev => ({
                    ...prev,
                    currentStep: 'completed'
                }));
            } else {
                // Only show as failed if we truly failed
                setProgress(prev => ({
                    ...prev,
                    currentStep: 'failed',
                    errors: [...prev.errors, 'Import failed: No content was successfully imported.']
                }));
            }

        } catch (error) {
            console.error('Import process failed:', error);
            
            // Check if we at least created the space successfully before deciding if this is a failure
            const hasCreatedSpace = progress.createdSpace;
            const hasCreatedPages = progress.createdPages && progress.createdPages.length > 0;
            
            if (hasCreatedSpace && hasCreatedPages) {
                // We successfully created the space and some pages, so consider this a success
                // even if there were some errors along the way
                console.log('Import partially successful - space and some pages created despite errors');
                setProgress(prev => ({
                    ...prev,
                    currentStep: 'completed'
                }));
            } else {
                // Provide more helpful error messages for true failures
                let errorMessage = error.message;
                if (error.message.includes('User information is missing')) {
                    errorMessage = 'Authentication error: Please log in and try again.';
                } else if (error.message.includes('Cannot read properties of undefined')) {
                    errorMessage = 'Authentication error: User session may have expired. Please refresh and try again.';
                } else if (error.message.includes('permission-denied') || error.message.includes('insufficient permissions')) {
                    errorMessage = 'Permission error: Your account does not have sufficient permissions to create spaces. Please contact support.';
                }
                
                setProgress(prev => ({
                    ...prev,
                    currentStep: 'failed',
                    errors: [...prev.errors, `Import failed: ${errorMessage}`]
                }));
            }
        }
    };

    const createSpace = async () => {
        try {
            // Use userInfo prop if available, otherwise use currentUser from Firebase Auth
            const user = userInfo || currentUser;
            
            // Validate user
            if (!user || !user.uid) {
                throw new Error("User information is missing or invalid. Please ensure you are logged in.");
            }
            
            console.log('Creating space with user:', user.uid);
            console.log('Space data:', spaceData);
            
            const spaceData = {
                name: spaceConfig.spaceName,
                description: spaceConfig.spaceDescription || "",
                created_by: user.uid,
                created_at: new Date(),
                members: [user.uid],
                is_public: spaceConfig.makePublic,
                root_page_title: spaceConfig.rootPageTitle || spaceConfig.spaceName,
                imported_from: 'confluence_zip',
                imported_at: new Date()
            };

            const spaceRef = await addDoc(collection(db, "spaces"), spaceData);
            console.log('Created space:', spaceRef.id);
            return spaceRef.id;
        } catch (error) {
            console.error('Space creation error:', error);
            
            // Handle specific Firebase errors
            if (error.code === 'permission-denied') {
                throw new Error(`Permission denied: Unable to create space. Please check your account permissions or contact support.`);
            } else if (error.code === 'unauthenticated') {
                throw new Error(`Authentication error: Please log in and try again.`);
            } else {
                throw new Error(`Failed to create space: ${error.message}`);
            }
        }
    };

    const processImages = async () => {
        const imageMap = {};
        setProgress(prev => ({
            ...prev,
            currentStep: 'processing_images'
        }));

        for (let i = 0; i < extractedData.images.length; i++) {
            const image = extractedData.images[i];
            
            setProgress(prev => ({
                ...prev,
                currentFile: image.name,
                processedImages: i
            }));

            try {
                // Upload image to Firebase Storage
                const imageRef = ref(storage, `space_images/${Date.now()}_${image.name}`);
                await uploadString(imageRef, image.content, 'data_url');
                const downloadURL = await getDownloadURL(imageRef);
                
                // Map original path to new URL
                imageMap[image.name] = downloadURL;
                imageMap[image.path] = downloadURL;
                
                // Track uploaded file for potential cleanup
                setUploadedFileRefs(prev => [...prev, imageRef]);
                
            } catch (error) {
                console.error(`Failed to upload image ${image.name}:`, error);
                setProgress(prev => ({
                    ...prev,
                    errors: [...prev.errors, `Failed to upload image: ${image.name}`]
                }));
            }
        }

        setProgress(prev => ({
            ...prev,
            processedImages: extractedData.images.length
        }));

        return imageMap;
    };

    const processFiles = async (selectedFiles, spaceId, imageMap) => {
        setProgress(prev => ({
            ...prev,
            currentStep: 'processing_files'
        }));

        // Build hierarchy map if preserving structure
        const hierarchyMap = spaceConfig.preserveHierarchy ? 
            buildHierarchyMap(selectedFiles) : {};

        const createdPages = [];

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            
            setProgress(prev => ({
                ...prev,
                currentFile: file.name,
                processedFiles: i
            }));

            try {
                const pageId = await processHtmlFile(file, spaceId, imageMap, hierarchyMap);
                createdPages.push({ id: pageId, name: file.name, path: file.path });
                
            } catch (error) {
                console.error(`Failed to process file ${file.name}:`, error);
                setProgress(prev => ({
                    ...prev,
                    errors: [...prev.errors, `Failed to process file: ${file.name} - ${error.message}`]
                }));
            }
        }

        setProgress(prev => ({
            ...prev,
            processedFiles: selectedFiles.length,
            createdPages: createdPages
        }));
    };

    const buildHierarchyMap = (files) => {
        const hierarchyMap = {};
        
        files.forEach(file => {
            const pathParts = file.path.split('/');
            const directory = pathParts.slice(0, -1).join('/');
            
            if (directory) {
                hierarchyMap[file.path] = {
                    directory: directory,
                    level: pathParts.length - 1,
                    parentPath: pathParts.slice(0, -2).join('/')
                };
            }
        });
        
        return hierarchyMap;
    };

    const convertHtmlToQuillDelta = async (htmlContent) => {
        // Create a temporary container
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '800px'; // Set a reasonable width
        document.body.appendChild(tempContainer);

        try {
            // Create a temporary Quill instance with the same modules as ViewAndEditUploadedFile
            const tempQuill = new Quill(tempContainer, {
                theme: 'snow',
                modules: {
                    toolbar: false,
                    history: {
                        delay: 1000,
                        maxStack: 100,
                        userOnly: true
                    },
                    imageResize: {
                        parchment: Quill.import("parchment"),
                        modules: ["Resize", "DisplaySize"],
                    },
                    table: false,
                    "table-better": {
                        language: "en_US",
                        menus: [
                            "column",
                            "row", 
                            "merge",
                            "table",
                            "cell",
                            "wrap",
                            "copy",
                            "delete",
                        ],
                        toolbarTable: true,
                        operationMenu: {
                            items: {
                                insertColumnLeft: { text: 'Insert column left' },
                                insertColumnRight: { text: 'Insert column right' },
                                insertRowUp: { text: 'Insert row above' },
                                insertRowDown: { text: 'Insert row below' },
                                mergeCells: { text: 'Merge cells' },
                                unmergeCells: { text: 'Unmerge cells' },
                                deleteColumn: { text: 'Delete column' },
                                deleteRow: { text: 'Delete row' },
                                deleteTable: { text: 'Delete table' }
                            }
                        }
                    }
                }
            });

            // Use Quill's clipboard to convert HTML to Delta and then back to HTML
            // This ensures proper Quill formatting
            tempQuill.clipboard.dangerouslyPasteHTML(0, htmlContent);
            
            // Add a small delay to ensure proper processing
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Get the Delta representation first, then convert back to HTML
            // This ensures the content is fully processed through Quill's pipeline
            const delta = tempQuill.getContents();
            
            // Clear and set content using Delta to ensure proper processing
            tempQuill.setContents(delta);
            
            // Add another small delay
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Now get the final HTML after full Quill processing
            const processedHtml = tempQuill.root.innerHTML;
            
            // Log details for debugging
            console.log("Converted HTML through Quill processing");
            console.log("Original HTML length:", htmlContent.length);
            console.log("Processed HTML length:", processedHtml.length);
            console.log("Delta operations:", delta.ops?.length || 0);
            
            // Check if we have code elements
            const codeElements = tempQuill.root.querySelectorAll('code');
            console.log("Code elements found:", codeElements.length);
            if (codeElements.length > 0) {
                console.log("First code element:", codeElements[0].outerHTML);
                console.log("Code element classes:", codeElements[0].className);
            }
            
            // Check if we have tables
            const tableElements = tempQuill.root.querySelectorAll('table');
            console.log("Table elements found:", tableElements.length);
            if (tableElements.length > 0) {
                console.log("First table structure:", tableElements[0].outerHTML.substring(0, 200) + "...");
            }
            
            return processedHtml;
        } catch (error) {
            console.error("Error in Quill conversion:", error);
            // Fallback to original content if Quill processing fails
            return htmlContent;
        } finally {
            // Cleanup
            if (tempContainer.parentNode) {
                document.body.removeChild(tempContainer);
            }
        }
    };

    const processHtmlFile = async (file, spaceId, imageMap, hierarchyMap) => {
        // Get matching images for this specific file
        let pageImages = [];
        const pageId = extractPageIdFromFilename(file.name);
        if (pageId && extractedData.images) {
            pageImages = extractedData.images.filter(image => {
                const imagePath = image.path.toLowerCase();
                return imagePath.includes(`attachments/${pageId}/`);
            });
            console.log(`Found ${pageImages.length} images for page ${pageId}`);
        }

        // Use the shared HTML parsing utility to convert HTML to clean format
        const parsedHtml = await parseHTMLContent(file.content, pageImages);
        
        // Convert the parsed HTML to proper Quill format
        const quillContent = await convertHtmlToQuillDelta(parsedHtml);
        
        // Extract title from HTML or use filename
        const title = extractTitleFromHtml(file.content) || 
                     file.name.replace('.html', '').replace(/[-_]/g, ' ');

        // Determine parent page if preserving hierarchy
        let parentPageId = null;
        if (spaceConfig.preserveHierarchy && hierarchyMap[file.path]) {
            // For now, we'll create flat structure but store hierarchy info
            // In a full implementation, you'd need to process files in order 
            // and maintain parent-child relationships
        }

        console.log(`Saving page "${title}" with Quill-processed content`);

        // Use userInfo prop if available, otherwise use currentUser from Firebase Auth
        const user = userInfo || currentUser;
        
        // Validate user before creating page
        if (!user || !user.uid) {
            throw new Error("User information is missing or invalid. Please ensure you are logged in.");
        }

        const pageData = {
            title: title,
            content: quillContent,
            space_id: spaceId,
            created_by: user.uid,
            created_at: new Date(),
            updated_at: new Date(),
            is_public: spaceConfig.makePublic,
            imported_from: 'confluence_zip',
            original_path: file.path,
            hierarchy_info: hierarchyMap[file.path] || null,
            parent_page_id: parentPageId
        };

        try {
            const pageRef = await addDoc(collection(db, "pages"), pageData);
            return pageRef.id;
        } catch (error) {
            console.error('Page creation error:', error);
            
            // Handle specific Firebase errors
            if (error.code === 'permission-denied') {
                throw new Error(`Permission denied: Unable to create page "${title}". Please check your account permissions.`);
            } else if (error.code === 'unauthenticated') {
                throw new Error(`Authentication error: Please log in and try again.`);
            } else {
                throw new Error(`Failed to create page "${title}": ${error.message}`);
            }
        }
    };

    const extractPageIdFromFilename = (filename) => {
        // Extract page ID from filename like "Database-Tables_2628255748.html" or "2628255748.html"
        let match = filename.match(/_(\d+)\.html$/);
        if (!match) {
            match = filename.match(/(\d+)\.html$/);
        }
        return match ? match[1] : null;
    };

    const extractTitleFromHtml = (htmlContent) => {
        const titleMatch = htmlContent.match(/<title[^>]*>([^<]+)<\/title>/i);
        if (titleMatch) {
            return titleMatch[1].trim();
        }
        
        const h1Match = htmlContent.match(/<h1[^>]*>([^<]+)<\/h1>/i);
        if (h1Match) {
            return h1Match[1].trim();
        }
        
        return null;
    };

    const handleComplete = () => {
        if (onComplete) {
            setIsTransitioning(true);
            setTimeout(() => {
                onComplete(progress.createdSpace, progress.createdPages);
            }, 150);
        }
    };

    const handleBackClick = () => {
        if (onBack && !isProcessing) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    const getProgressPercentage = () => {
        if (progress.currentStep === 'completed') return 100;
        if (progress.currentStep === 'failed') return 0;
        
        const totalSteps = 3; // creating_space, processing_images, processing_files
        let completedSteps = 0;
        
        if (progress.createdSpace) completedSteps++;
        if (progress.processedImages === progress.totalImages) completedSteps++;
        if (progress.processedFiles === progress.totalFiles) completedSteps++;
        
        return Math.round((completedSteps / totalSteps) * 100);
    };

    const getStepDescription = () => {
        switch (progress.currentStep) {
            case 'initializing':
                return 'Initializing import process...';
            case 'creating_space':
                return 'Creating space...';
            case 'processing_images':
                return `Processing images... (${progress.processedImages}/${progress.totalImages})`;
            case 'processing_files':
                return `Processing files... (${progress.processedFiles}/${progress.totalFiles})`;
            case 'completed':
                return 'Import completed successfully!';
            case 'failed':
                return 'Import failed. Please check the errors below.';
            default:
                return 'Processing...';
        }
    };

    // Show loading while waiting for authentication
    if (!currentUser && !userInfo) {
        return (
            <div className="ImportDocument__main-div">
                <div className="ImportDocument__content">
                    <div className="ImportDocument__loading-animation-div">
                        <i className="fas fa-spinner fa-spin DocumentationHomeComponent__loading-animation-icon"></i>
                        <p>Checking authentication...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`ImportDocument__main-div ${isTransitioning ? 'transitioning' : ''}`}>
            <div className="ImportDocument__content">
                <h2>Processing Space Import</h2>
                
                {/* Show loading while initializing or processing */}
                {(isProcessing && progress.currentStep === 'initializing') ? (
                    <div className="ImportDocument__loading-animation-div">
                        <i className="fas fa-spinner fa-spin DocumentationHomeComponent__loading-animation-icon"></i>
                        <p>Initializing import process...</p>
                    </div>
                ) : (
                    <div className="ImportDocument__progress-section">
                        <div className="ImportDocument__progress-header">
                            <h3>{getStepDescription()}</h3>
                            {progress.currentFile && (
                                <p className="ImportDocument__current-file">
                                    Current: {progress.currentFile}
                                </p>
                            )}
                        </div>

                        <div className="ImportDocument__progress-bar">
                            <div 
                                className="ImportDocument__progress-fill"
                                style={{ width: `${getProgressPercentage()}%` }}
                            ></div>
                        </div>

                        <div className="ImportDocument__progress-stats">
                            <div className="ImportDocument__stat">
                                <strong>{progress.processedFiles}</strong> / <strong>{progress.totalFiles}</strong>
                                <span>Files Processed</span>
                            </div>
                            {spaceConfig.processImages && (
                                <div className="ImportDocument__stat">
                                    <strong>{progress.processedImages}</strong> / <strong>{progress.totalImages}</strong>
                                    <span>Images Processed</span>
                                </div>
                            )}
                        </div>

                        {progress.errors.length > 0 && (
                            <div className="ImportDocument__errors">
                                <h4>Errors encountered:</h4>
                                <ul>
                                    {progress.errors.map((error, index) => (
                                        <li key={index} className="ImportDocument__error-item">
                                            {error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {progress.currentStep === 'completed' && (
                            <div className="ImportDocument__completion-summary">
                                <div className="ImportDocument__success-message">
                                    <i className="fas fa-check-circle"></i>
                                    <h3>Import Completed Successfully!</h3>
                                </div>
                                
                                <div className="ImportDocument__results">
                                    <p><strong>Space Created:</strong> {spaceConfig.spaceName}</p>
                                    <p><strong>Pages Imported:</strong> {progress.createdPages.length}</p>
                                    <p><strong>Images Processed:</strong> {progress.processedImages}</p>
                                    {progress.errors.length > 0 && (
                                        <p><strong>Errors:</strong> {progress.errors.length} (see above)</p>
                                    )}
                                </div>

                                <button 
                                    onClick={handleComplete}
                                    className="ImportDocument__complete-button"
                                >
                                    View Imported Space
                                </button>
                            </div>
                        )}

                        {progress.currentStep === 'failed' && (
                            <div className="ImportDocument__failure-message">
                                <i className="fas fa-exclamation-triangle"></i>
                                <h3>Import Failed</h3>
                                <p>The import process encountered critical errors. Please check the error messages above and try again.</p>
                                
                                <button 
                                    onClick={handleBackClick}
                                    className="ImportDocument__retry-button"
                                    disabled={isProcessing}
                                >
                                    Go Back to Configuration
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Back Arrow or Loading Icon */}
            {isProcessing && progress.currentStep !== 'completed' ? (
                <div className="ImportDocument__arrow ImportDocument__arrow--back" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'default'
                }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '18px' }}></i>
                </div>
            ) : (
                !isProcessing && progress.currentStep !== 'completed' && (
                    <div 
                        className="ImportDocument__arrow ImportDocument__arrow--back" 
                        onClick={handleBackClick}
                        title="Go back to configuration"
                        aria-label="Go back to configuration"
                    >
                        ❮
                    </div>
                )
            )}
        </div>
    );
}

export default ProcessSpaceImport;
